/**
 * React Query mutations for play operations.
 * Provides optimistic updates for delete, update, and duplicate operations.
 */

import { useCallback } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { deletePlay as apiDeletePlay, playKeys } from '@/api/queries/PlayQueries'

interface UsePlayMutationsOptions {
	playbookId: string
	onDeleteError?: (error: Error) => void
	onDeleteSuccess?: (playId: string) => void
}

interface UsePlayMutationsReturn {
	deletePlay: (playId: string) => Promise<void>
	isDeleting: boolean
}

/**
 * Hook that provides play mutation functions with optimistic updates.
 *
 * The deletePlay function will:
 * 1. Send the delete request to the server
 * 2. On success: invalidate React Query caches and call onDeleteSuccess
 * 3. On error: call onDeleteError
 *
 * Note: This mutation invalidates React Query caches. For components using
 * local state (like PlayEditorPage), handle the local state update in the
 * onDeleteSuccess callback.
 *
 * @example
 * ```tsx
 * const { deletePlay } = usePlayMutations({
 *   playbookId,
 *   onDeleteError: (error) => console.error('Delete failed:', error),
 *   onDeleteSuccess: (playId) => {
 *     // Update local state
 *     setPlays(prev => prev.filter(p => p.id !== playId))
 *   },
 * })
 *
 * await deletePlay(playId)
 * ```
 */
export function usePlayMutations({
									 playbookId,
									 onDeleteError,
									 onDeleteSuccess,
								 }: UsePlayMutationsOptions): UsePlayMutationsReturn {
	const queryClient = useQueryClient()

	const deletePlayMutation = useMutation({
		mutationFn: async (playId: string) => {
			await apiDeletePlay(playId)
		},
		onSuccess: (_, playId) => {
			// Invalidate all play-related queries
			queryClient.invalidateQueries({ queryKey: playKeys.list(playbookId) })
			queryClient.invalidateQueries({ queryKey: playKeys.content(playId) })
			onDeleteSuccess?.(playId)
		},
		onError: (error) => {
			console.error('Failed to delete play:', error)
			onDeleteError?.(error as Error)
		},
	})

	const deletePlay = useCallback(
		async (playId: string): Promise<void> => {
			await deletePlayMutation.mutateAsync(playId)
		},
		[deletePlayMutation],
	)

	return {
		deletePlay,
		isDeleting: deletePlayMutation.isPending,
	}
}
